import { useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useOverlay } from '../ui/Overlay';
import './PairCeremony.css';

/**
 * PairCeremony — the ceremonial pairing moment (card 2NuQ8dd2JMqs).
 *
 * When the invited partner joins, BOTH phones get the same celebration: a
 * full-screen moment whose milestone card reads "You're in 🍐 · Oct 2026",
 * and behind it the very same line has just been filed in the Vault as a
 * shared memory (the room writes it server-side, so it survives a reload and
 * exists on both devices). It replaces the passive "Waiting…" state — the
 * first partner hears about the pairing from the room's `paired` frame, the
 * joiner from its own join response.
 *
 * Shown from in-memory state only: a page reload never replays a moment that
 * already happened. Auto-dismisses; a tap, Enter, Space or Escape dismisses
 * it sooner. Portalled to body like the tour — #chat-screen ends in a
 * transform, which would otherwise become the containing block for
 * position: fixed.
 */
export interface PairCeremonyProps {
  onDone: () => void;
}

/** Mirrors pairMilestone() in shared/types.ts — the web bundle types its own
 *  surface rather than importing shared (same convention as INVITE_ALPHABET).
 *  Computed from the clock at the moment of pairing, never stored. */
export function pairMilestone(now: Date = new Date()): string {
  const month = now.toLocaleDateString('en-AU', { month: 'short' });
  return `You're in 🍐 · ${month} ${now.getFullYear()}`;
}

/** Long enough to read two lines, short enough never to feel like a hold. */
const AUTO_DISMISS_MS = 4600;
/** Time for the fade-out before React unmounts the overlay. */
const LEAVE_MS = 320;
const SPARK_COUNT = 10;

export function PairCeremony({ onDone }: PairCeremonyProps) {
  const [leaving, setLeaving] = useState(false);
  /** The one close(), shared by the timers, the keyboard and the tap — the
   *  effect owns its lifetime, the click handlers only ask it to run. */
  const closeRef = useRef<() => void>(() => {});

  /* The ceremony and the tour are the two overlays that must be able to
     coexist, so both take the ONE shared slot: whichever mounts first holds
     it, the other waits instead of stacking on top with two focus traps and
     two Escape handlers (UX item 6). App mounts this one first, so a newly
     paired couple sees the celebration and the tour follows it.

     `granted` is what starts the moment — while the ceremony waits its turn
     the auto-dismiss clock has not begun, so it can never fade out unseen.

     `open` goes false the instant the moment ends: the slot and the modal
     behaviour (focus trap, Escape, inert background, scroll lock) belong to
     whoever holds the slot next, and the 320ms fade below is only paint — it
     must not keep owning the page while another overlay has it. */
  const { granted, panelRef, panelProps, rootProps } = useOverlay({
    open: !leaving,
    onClose: () => closeRef.current(),
  });

  useEffect(() => {
    if (!granted) return;
    let gone = false;
    closeRef.current = () => {
      if (gone) return;
      gone = true;
      setLeaving(true);
      window.setTimeout(onDone, LEAVE_MS);
    };
    const close = () => closeRef.current();
    const auto = window.setTimeout(close, AUTO_DISMISS_MS);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(auto);
      document.removeEventListener('keydown', onKey);
    };
  }, [granted, onDone]);

  /* The slot and the modal behaviour are gone the moment the moment ends;
     the 320ms fade is only paint. Render it WITHOUT the overlay contract —
     no dialog role, no aria-modal, no overlay root — so an overlay that has
     since taken the slot keeps its own inert background and the exiting
     celebration is just background content to it. */
  const fading = leaving && !granted;
  if (!granted && !leaving) return null;

  return createPortal(
    <div
      {...(fading ? {} : rootProps)}
      className={`pair-ceremony${leaving ? ' is-leaving' : ''}`}
      id="pair-ceremony"
      aria-label={fading ? undefined : 'You are paired'}
      onClick={() => closeRef.current()}
    >
      <div
        {...(fading ? {} : panelProps)}
        ref={fading ? undefined : (panelRef as RefObject<HTMLDivElement | null>)}
        aria-label={fading ? undefined : 'You are paired'}
        className="pair-ceremony-card"
        onClick={() => closeRef.current()}
      >
        <span className="pair-ceremony-ring" aria-hidden="true" />
        {Array.from({ length: SPARK_COUNT }, (_, i) => (
          <span
            key={i}
            className="pair-ceremony-spark"
            aria-hidden="true"
            style={{ ['--i' as string]: i }}
          />
        ))}
        <span className="pair-ceremony-pear" aria-hidden="true">🍐</span>
        <p className="pair-ceremony-milestone" data-testid="pair-ceremony-milestone">
          {pairMilestone()}
        </p>
        <p className="pair-ceremony-sub">Both of you. One space — it starts now.</p>
        <button
          type="button"
          className="pair-ceremony-continue"
          id="btn-ceremony-continue"
          onClick={e => {
            e.stopPropagation();
            closeRef.current();
          }}
        >
          Continue
        </button>
      </div>
    </div>,
    document.body,
  );
}
